import { useCallback, useMemo, useRef } from "react";
import {
  type BuyerMinimumRequirementReviewResponsePatchDto,
  type BuyerBidDetailDto,
  useUpdateBuyerMinimumRequirementReviewMutation,
} from "../../../autogen/bff-api";
import { useCreateEditor, useEditor } from "../../../common/editing/save-coordinator";
import { useApiError } from "../../../common/errors/useApiError";

const DEBOUNCE_MS = 300;

interface EditorState extends Record<string, unknown> {
  verdicts: Record<string, boolean | null>;
  comments: Record<string, string>;
}

type EditorFields = Partial<EditorState> & { forDeliverySubmittedAt?: string };

interface UseEditor {
  state: EditorState;
  setVerdict: (minimumRequirementId: string, value: boolean | null) => void;
  setComment: (minimumRequirementId: string, value: string) => void;
  isSaving: boolean;
}

export const useBuyerMinimumRequirementReviewEditor = (
  eventId: string,
  bidId: string,
  detail: BuyerBidDetailDto,
): UseEditor => {
  const { trigger } = useApiError();
  const [update] = useUpdateBuyerMinimumRequirementReviewMutation();
  const entityId = `bid-minimum-requirement-review:${bidId}`;
  const detailRef = useRef(detail);
  detailRef.current = detail;

  const responses = detail.bid.minimumRequirementResponses;
  const initialState = useMemo(() => toEditorState(responses), [responses]);

  const save = useCallback(
    async (fields: EditorFields): Promise<EditorState> => {
      const updated = await update({
        eventId,
        bidId,
        buyerMinimumRequirementReviewPatchDto: {
          responses: toPatchResponses(fields),
          forDeliverySubmittedAt: fields.forDeliverySubmittedAt,
        },
      }).unwrap();
      return toEditorState(updated.bid.minimumRequirementResponses);
    },
    [bidId, eventId, update],
  );

  useCreateEditor<EditorState, EditorFields>(entityId, { initialState, save, onError: trigger });
  const { state, updateField, entityStatus } = useEditor<EditorState, EditorFields>(entityId);

  const setVerdict = useCallback(
    (minimumRequirementId: string, value: boolean | null) =>
      updateField(
        { verdicts: { [minimumRequirementId]: value }, forDeliverySubmittedAt: detailRef.current.bid.submittedAt ?? undefined },
        { debounceMs: DEBOUNCE_MS },
      ),
    [updateField],
  );

  const setComment = useCallback(
    (minimumRequirementId: string, value: string) =>
      updateField(
        { comments: { [minimumRequirementId]: value }, forDeliverySubmittedAt: detailRef.current.bid.submittedAt ?? undefined },
        { debounceMs: DEBOUNCE_MS },
      ),
    [updateField],
  );

  return { state, setVerdict, setComment, isSaving: entityStatus === "saving" };
};

const toEditorState = (responses: BuyerBidDetailDto["bid"]["minimumRequirementResponses"]): EditorState => {
  const verdicts: Record<string, boolean | null> = {};
  const comments: Record<string, string> = {};
  for (const r of responses ?? []) {
    verdicts[r.minimumRequirementId] = r.buyerVerdict ?? null;
    comments[r.minimumRequirementId] = r.buyerComment ?? "";
  }
  return { verdicts, comments };
};

const toPatchResponses = (fields: EditorFields): BuyerMinimumRequirementReviewResponsePatchDto[] => {
  const byId = new Map<string, BuyerMinimumRequirementReviewResponsePatchDto>();
  const responseFor = (minimumRequirementId: string) => byId.get(minimumRequirementId) ?? { minimumRequirementId };
  for (const [minimumRequirementId, buyerVerdict] of Object.entries(fields.verdicts ?? {})) {
    byId.set(minimumRequirementId, { ...responseFor(minimumRequirementId), buyerVerdict });
  }
  for (const [minimumRequirementId, buyerComment] of Object.entries(fields.comments ?? {})) {
    byId.set(minimumRequirementId, { ...responseFor(minimumRequirementId), buyerComment });
  }
  return Array.from(byId.values());
};
